import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"

import { formatDateDataGrid } from "src/utils/format-date"
import { DATE_FORMAT_SHOW } from "src/utils/constants"
import { DATE_FORMAT } from "src/utils/constants"
import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterEmissaoRendaFixaDataAniversarioTable = (props) => {
    const datalakeService = new DatalakeService()

    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null
    })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)

        datalakeService
            .loadPaginationEndpoint(props.endpoint)
            .then((response) => {
                const { results, count, next, previous } = response.body
                const list = results.map((obj) => ({ ...obj, id: obj.ID }))

                setPagination({ count, next, previous })
                setData(list)
            })
            .catch(props.callbackError)
            .finally(() => props.loading(false))
    }

    const createRow = async () => {
        const body = {
            RendaFixaCaracteristicasID: null,
            IDFluxoEvento: 0,
            VersaoDocumento: 0,
            DataReferencia: dayjs().format(DATE_FORMAT),
            DataAniversarioAnterior: dayjs().format(DATE_FORMAT),
            DataAniversario: dayjs().format(DATE_FORMAT),
            CorrigeDataAniversario: "N/A",
            AniversarioPeriodico: 0
        }

        props.loading(true)

        return datalakeService
            .createTableEndpoint(props.endpoint, body)
            .then(() => loadData())
            .catch((err) => {
                props.loading(false)
                throw err
            })
            .finally(() => props.loading(false))
    }

    const updateRow = async (newRow) => {
        const {
            ID,
            RendaFixaCaracteristicasID,
            IDFluxoEvento,
            VersaoDocumento,
            DataReferencia,
            DataAniversarioAnterior,
            DataAniversario,
            CorrigeDataAniversario,
            AniversarioPeriodico
        } = newRow

        const body = {
            RendaFixaCaracteristicasID,
            IDFluxoEvento,
            VersaoDocumento,
            DataReferencia,
            DataAniversarioAnterior,
            DataAniversario,
            CorrigeDataAniversario,
            AniversarioPeriodico
        }

        props.loading(true)

        return datalakeService
            .updateTableEndpoint(props.endpoint, ID, body)
            .catch((err) => {
                props.loading(false)
                throw err
            })
            .finally(() => props.loading(false))
    }

    const deleteRow = async (row) => {
        const { ID } = row
        props.loading(true)

        return datalakeService
            .deleteTableEndpoint(props.endpoint, ID)
            .then(() => {
                setData(data.filter((item) => item.ID !== ID))
            })
            .catch((err) => {
                props.loading(false)
                throw err
            })
            .finally(() => props.loading(false))
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList
            .map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`)
            .join('&')

        props.loading(true)

        datalakeService
            .loadPaginationEndpoint(props.endpoint, query, limit, offset)
            .then((response) => {
                const { results, count, next, previous } = response.body
                const list = results.map((obj) => ({ ...obj, id: obj.ID }))

                setPagination({ count, next, previous })
                setData(list)
            })
            .finally(() => props.loading(false))
    }

    const changePage = async (endpoint) => {
        props.loading(true)

        datalakeService
            .paginate(endpoint)
            .then((response) => {
                const { results, count, next, previous } = response.body
                const list = results.map((obj) => ({ ...obj, id: obj.ID }))

                setPagination({ count, next, previous })
                setData(list)
            })
            .finally(() => props.loading(false))
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            minWidth: 80,
            disableColumnMenu: true,
            disableFilter: true
        },
        {
            field: 'RendaFixaCaracteristicasID',
            headerName: 'RendaFixaCaracteristicasID',
            minWidth: 220,
            editable: true,
            disableColumnMenu: true
        },
        {
            field: 'IDFluxoEvento',
            headerName: 'IDFluxoEvento',
            minWidth: 160,
            editable: true,
            disableColumnMenu: true
        },
        {
            field: 'VersaoDocumento',
            headerName: 'VersaoDocumento',
            minWidth: 160,
            editable: true,
            disableColumnMenu: true
        },
        {
            field: 'DataReferencia',
            headerName: 'DataReferencia',
            minWidth: 180,
            typeComponent: 'date',
            editable: true,
            disableColumnMenu: true,
            renderCell: (params) =>
                params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'DataAniversarioAnterior',
            headerName: 'DataAniversarioAnterior',
            minWidth: 220,
            typeComponent: 'date',
            editable: true,
            disableColumnMenu: true,
            renderCell: (params) =>
                params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'DataAniversario',
            headerName: 'DataAniversario',
            minWidth: 180,
            typeComponent: 'date',
            editable: true,
            disableColumnMenu: true,
            renderCell: (params) =>
                params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'CorrigeDataAniversario',
            headerName: 'CorrigeDataAniversario',
            minWidth: 220,
            editable: true,
            disableColumnMenu: true
        },
        {
            field: 'AniversarioPeriodico',
            headerName: 'AniversarioPeriodico',
            minWidth: 200,
            editable: true,
            disableColumnMenu: true
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            minWidth: 150,
            disableFilter: true,
            disableColumnMenu: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            minWidth: 150,
            disableFilter: true,
            disableColumnMenu: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <ParameterPaginationTable
            columns={columns}
            data={data}
            setData={setData}
            createRow={createRow}
            updateRow={updateRow}
            deleteRow={deleteRow}
            pagination={pagination}
            onChangePage={changePage}
            filterFunction={onFilter}
            customFilter
        />
    )
}

ParameterEmissaoRendaFixaDataAniversarioTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterEmissaoRendaFixaDataAniversarioTable
